import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Trans, Plural } from '@lingui/react';
import { t } from '@lingui/macro';
import { useLocalisation } from '@3plearning/lingui-cdn';

// Components
import Table from '../table/table';
import MainListBody from './main-list-body';
import Checkbox, { LABEL_STYLES } from '../checkbox/checkbox';
import Button, { BUTTON_STYLES } from '../button/button';
import EmptyDataMessage from '../empty-data-message/empty-data-message';
import StudentChangeGroup from '../student-change-group/student-change-group';

// Helpers
import { stripCharacters, compareByLastName, compareValues } from '../../helpers/utils';
import { ALL_GROUP_ID } from '../../helpers/enums';
import SelectLabel from '../select-label/select-label';
import Tooltip from '../tooltip/tooltip';
import Badge from '../badge/badge';

let initialLoad = true;

function MainList(props) {
    const { i18n } = useLocalisation();
    const {
        hasContentLoaded,
        classesLength,
        classGroupStudents,
        classGroups,
        changePassword,
        shownPassword,
        setShownPassword,
        // change group props
        updateCurrentClassId,
        selectGroupHandler,
        currentClassId,
        productId,
        showNewCourses,
        // Checkbox related props
        mainCheckAllIsSelected,
        mainSelectedStudentIds,
        selectCheckboxStudent,
        selectAllCheckboxStudent
    } = props;

    // Filtered states
    const [filteredClassGroups, setFilteredClassGroups] = useState([]);
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [hasFiltered, setHasFiltered] = useState(false);
    const [isNameFilterAscending, setIsNameFilterAscending] = useState(true);
    const [isGroupFilterAscending, setIsGroupFilterAscending] = useState(true);
    const [isSortedByName, setIsSortedByName] = useState(true);

    useEffect(() => {
        const filtered = classGroupStudents.filter(student => student.isVisible);
        setFilteredStudents(filtered);
    }, [classGroupStudents]);

    useEffect(() => {
        // Variables
        const localFilteredClassGroups = classGroups.filter((group) => {
            // Remove the instance of the "All groups" from the list of values in the student row
            if (group.groupId > ALL_GROUP_ID) {
                return group;
            }
            return false;
        });
        setFilteredClassGroups(localFilteredClassGroups);
    }, [classGroups]);

    const [isChangeGroupModalVisible, setIsChangeGroupModalVisible] = useState(false);
    const [isChangeGroupDisabled, setIsChangeGroupDisabled] = useState(false);
    const [changeGroupErrorMessage, setChangeGroupErrorMessage] = useState(null);
    const [changeGroupData, setChangeGroupData] = useState(null);
    // state to hide the modal
    const [isModalVisible, setIsModalVisible] = useState(false);

    useEffect(() => {
        setChangeGroupErrorMessage('');
    }, [isChangeGroupModalVisible]);

    const applyChangeGroupHandler = async () => {
        if (changeGroupData !== null && isChangeGroupDisabled === false) {
            const { newGroupId, userIds, classId } = changeGroupData;
            /*
                In order to prevent multiple buttons to be clicked,
                set the change group to be disabled, until it's completed
             */
            setIsChangeGroupDisabled(true);
            const cleanedUserIds = userIds.map(userId => parseInt(userId, 10));
            const selectGroupResponse = await selectGroupHandler({
                newGroupId: parseInt(newGroupId, 10),
                userIds: cleanedUserIds,
                classId
            });

            if (selectGroupResponse === true) {
                await updateCurrentClassId({ productId, classId: currentClassId });
                // Reset the modal + data
                setIsChangeGroupModalVisible(false);
                setChangeGroupData(null);
            } else {
                setChangeGroupErrorMessage(i18n._(t`Error Message - Unable to proceed`));
            }

            // Resetting the select on its initial value. BA 2020-05-04
            // TODO: passing through a ref?
            // eslint-disable-next-line no-undef
            document.getElementById('groupBulk').value = 0;
            setIsChangeGroupDisabled(false);
        } else {
            /*
                do nothing,
                as the call is still happening, prevent from keyboard users to action the button multiple times
            */
        }
    };

    const changeGroup = () => {
        if (isModalVisible) {
            // To show modal for the first time only
            setIsChangeGroupModalVisible(true);
            // To hide modal after the first time
            setIsModalVisible(false);
        } else {
            // To apply changes in the group for the student without displaying modal
            applyChangeGroupHandler();
            // To retain the students list in desc order when the group is changed
            if (isSortedByName) {
                if (isNameFilterAscending === false) {
                    const filterStudentsByLastName = filteredStudents.sort((...arg) => (
                        compareByLastName(...arg, true)
                    ));
                    setFilteredStudents(filterStudentsByLastName);
                }
            } else {
                const filterStudentsByGroup =
                filteredStudents.sort(compareValues('groupName', !isGroupFilterAscending));
                setFilteredStudents(filterStudentsByGroup);
            }
        }
    };

    useEffect(() => {
        changeGroup();
    }, [changeGroupData]);

    const showChangeGroupBulkModal = ({ newGroupId, userIds }) => {
        setChangeGroupData({ newGroupId, userIds, classId: currentClassId });
        if (initialLoad) {
            setIsModalVisible(true);
            initialLoad = false;
        }
        changeGroup();
    };

    const closeChangeGroupModal = () => {
        setChangeGroupData(null);
        setIsChangeGroupModalVisible(false);
    };

    // Sorting Handlers
    const sortingNameCallback = () => {
        const filterStudentsByLastName = filteredStudents.sort((...arg) => (
            compareByLastName(...arg, isNameFilterAscending)
        ));
        setFilteredStudents(filterStudentsByLastName);
        setHasFiltered(prevState => !prevState);
        setIsNameFilterAscending(prevState => !prevState);
        setIsSortedByName(true);
    };

    const sortingGroupCallback = () => {
        const filterStudentsByGroup = filteredStudents.sort(compareValues('groupName', isGroupFilterAscending));
        setFilteredStudents(filterStudentsByGroup);
        setHasFiltered(prevState => !prevState);
        setIsGroupFilterAscending(prevState => !prevState);
        setIsSortedByName(false);
    };

    const changeGroupBulkHandler = (e) => {
        const newGroupId = e.target.value;
        if (newGroupId !== '0') {
            showChangeGroupBulkModal({ newGroupId, userIds: mainSelectedStudentIds });
        }
    };

    // Cell elements (header/body)
    const listOfHeaders = [
        {
            key: stripCharacters(i18n._(t`Header - Select All`)),
            importedClasses: 'table__cell--checkbox',
            content: (
                <Checkbox
                    uid="consoleSelectAll"
                    labelText={<Trans id={'Header - Select All'} />}
                    inputType={LABEL_STYLES.hidden}
                    isChecked={mainCheckAllIsSelected}
                    callback={selectAllCheckboxStudent}
                />
            )
        },
        {
            key: stripCharacters(i18n._(t`Header - Students`)),
            importedClasses: 'table__cell--header-name',
            ariaProps: {
                'aria-sort': isNameFilterAscending ? 'ascending' : 'descending'
            },
            content: (
                <Button
                    buttonStyle={BUTTON_STYLES.iconText}
                    importedClasses={`button--sort sorting-cell${isSortedByName ? ' button--sort-active' : ''}`}
                    callback={sortingNameCallback}
                    visibleText={(
                        <Plural
                            id={'Header - Students - Plural'}
                            value={mainSelectedStudentIds.length}
                            one="Student (#)"
                            other="Students (#)"
                        />
                    )}
                    iconName={isNameFilterAscending ? 'sortup' : 'sortdown'}
                />
            )
        },
        {
            key: stripCharacters(i18n._(t`Header - Username`)),
            importedClasses: 'table__cell--header-detail',
            content: <Trans id={'Header - Username'} />
        },
        {
            key: stripCharacters(i18n._(t`Info - Get more info`)),
            importedClasses: 'table__cell--header-password',
            content: <Trans id={'Header - Password'} />
        },
        {
            key: stripCharacters(i18n._(t`Header - Group`)),
            importedClasses: 'table__cell--header-group',
            ariaProps: {
                'aria-sort': isGroupFilterAscending ? 'ascending' : 'descending'
            },
            content: (
                <Fragment>
                    <Button
                        buttonStyle={BUTTON_STYLES.iconText}
                        importedClasses={`button--sort sorting-cell${isSortedByName ? '' : ' button--sort-active'}`}
                        callback={sortingGroupCallback}
                        visibleText={(
                            <Trans id={'Header - Group'} />
                        )}
                        iconName={isGroupFilterAscending ? 'sortdown' : 'sortup'}
                    />
                    <div className="table__buttons-container">
                        <Badge
                            badgeId="tableGroupHeader"
                            assetTitle=""
                            visibleText={i18n._(t`Notification - New`)} />
                        <SelectLabel
                            id="groupBulk"
                            srLabel={i18n._(t`Groups - Change group for selected students`)}
                            selectHandler={changeGroupBulkHandler}
                            propertyName="groupId"
                            visibleProperty="name"
                            listOfOptions={[
                                {
                                    groupId: 0,
                                    name: `${i18n._(t`Groups - Move selected`)} (${mainSelectedStudentIds.length})`
                                },
                                ...filteredClassGroups
                            ]}
                            isSelectDisabled={mainSelectedStudentIds.length === 0}
                            importedClasses="select--header"
                        />
                        <Tooltip visibleTexts={[i18n._(t`Tooltips - main list - groups`)]} />
                    </div>
                </Fragment>
            )
        },
        {
            key: stripCharacters(i18n._(t`Header - Course`)),
            importedClasses: 'table__cell--header-course',
            content: <Trans id={'Header - Course'} />
        }
    ];

    const listOfRows = filteredStudents && filteredStudents.length > 0 ?
        filteredStudents.map(
            (row, index) => (
                <MainListBody
                    key={`${hasFiltered}-${row.userProfileId}`}
                    userId={row.userProfileId}
                    index={index}
                    courseName={row.courseName}
                    upfCourseName={row.upfCourseName}
                    conquestCourseName={row.conquestCourseName}
                    showNewCourses={showNewCourses}
                    username={row.username}
                    password={row.password}
                    productId={productId}
                    changePassword={changePassword}
                    shownPassword={shownPassword}
                    setShownPassword={setShownPassword}
                    filteredClassGroups={filteredClassGroups}
                    groupId={row.groupId}
                    firstName={row.firstName}
                    lastName={row.lastName}
                    oneRosterUserId={row.oneRosterUserId}
                    isSelected={mainSelectedStudentIds && mainSelectedStudentIds.includes(row.userProfileId)}
                    selectHandler={showChangeGroupBulkModal}
                    checkboxHandler={selectCheckboxStudent}
                />
            )
        ) :
        [
            (
                <EmptyDataMessage
                    key="emptyRow"
                    hasContentLoaded={hasContentLoaded}
                    classesLength={classesLength}
                    colSpan={listOfHeaders.length}
                />
            )
        ];

    return (
        <Fragment>
            <Table
                listOfHeaders={listOfHeaders}
                listOfRows={listOfRows}
                importedClasses="table--sticky"
            />
            {
                isChangeGroupModalVisible ? (
                    <StudentChangeGroup
                        closeHandler={closeChangeGroupModal}
                        applyHandler={applyChangeGroupHandler}
                        changeGroupErrorMessage={changeGroupErrorMessage}
                        productId={productId}
                    />
                ) : ''
            }
        </Fragment>
    );
}

MainList.propTypes = {
    hasContentLoaded: PropTypes.bool,
    classesLength: PropTypes.number,
    classGroupStudents: PropTypes.array.isRequired,
    classGroups: PropTypes.array.isRequired,
    mainCheckAllIsSelected: PropTypes.bool,
    mainSelectedStudentIds: PropTypes.array,
    selectCheckboxStudent: PropTypes.func,
    selectAllCheckboxStudent: PropTypes.func,
    changePassword: PropTypes.func.isRequired,
    shownPassword: PropTypes.string,
    setShownPassword: PropTypes.func,
    // change group props
    updateCurrentClassId: PropTypes.func,
    selectGroupHandler: PropTypes.func,
    currentClassId: PropTypes.number,
    productId: PropTypes.string,
    showNewCourses: PropTypes.bool
};

export default MainList;



// WEBPACK FOOTER //
// ./components/students-lists/main-list.js